import LegalPage from "@/components/LegalPage";

const sections = [
  {
    id: "what-are-cookies",
    title: "1. What are cookies?",
    body: (
      <>
        <p>
          Cookies are small text files that a website stores on your device when you visit.
          They are widely used to make websites work, to make them work more efficiently, and to
          provide information to the site owners. Similar technologies include localStorage,
          sessionStorage, and pixel tags.
        </p>
        <p>
          This Cookie Policy explains what cookies and similar technologies
          <a href="https://www.ashrenltd.com" className="underline"> www.ashrenltd.com</a> uses,
          why we use them, and how you can control them.
        </p>
      </>
    ),
  },
  {
    id: "categories",
    title: "2. Categories of cookies we use",
    body: (
      <>
        <p>
          The cookies and similar technologies used on our Site fall into four categories:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Strictly necessary</strong> — required for the Site to function (e.g.,
            remembering form state, maintaining your session, serving content across pages).
            These cannot be disabled without breaking parts of the Site.
          </li>
          <li>
            <strong>Preference / functional</strong> — remember choices you make (e.g., cookie
            consent acknowledgement stored in <em>localStorage</em>) to improve your experience
            on return visits.
          </li>
          <li>
            <strong>Performance / analytics</strong> — help us understand how visitors use the
            Site so we can improve it. We use PostHog for aggregated analytics and session
            recording. Where required by law, these only load after you give consent.
          </li>
          <li>
            <strong>Marketing</strong> — we do <em>not</em> currently use marketing cookies,
            advertising pixels, or cross-site tracking on this Site.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "specific-cookies",
    title: "3. Specific cookies and technologies we set",
    body: (
      <>
        <div className="overflow-x-auto -mx-5 md:mx-0">
          <table className="w-full text-sm border-collapse min-w-[640px]">
            <thead>
              <tr className="bg-[color:var(--primary-dark)] text-white text-left">
                <th className="px-3 py-2 font-semibold">Name / Technology</th>
                <th className="px-3 py-2 font-semibold">Category</th>
                <th className="px-3 py-2 font-semibold">Purpose</th>
                <th className="px-3 py-2 font-semibold">Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[color:var(--border)]">
                <td className="px-3 py-2"><code>ashren_cookie_consent</code> (localStorage)</td>
                <td className="px-3 py-2">Functional</td>
                <td className="px-3 py-2">Remembers that you have dismissed the cookie banner so it doesn&rsquo;t reappear.</td>
                <td className="px-3 py-2">12 months</td>
              </tr>
              <tr className="border-b border-[color:var(--border)]">
                <td className="px-3 py-2"><code>ph_*</code> (PostHog)</td>
                <td className="px-3 py-2">Analytics</td>
                <td className="px-3 py-2">Aggregated page-view and interaction analytics, error capture, and session recording. Used to improve site performance and content.</td>
                <td className="px-3 py-2">12 months</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Session storage (form state)</td>
                <td className="px-3 py-2">Strictly necessary</td>
                <td className="px-3 py-2">Temporarily holds form values while you&rsquo;re completing a page; cleared when you close the tab.</td>
                <td className="px-3 py-2">Session</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          We may update this list over time. Check back periodically or contact us for the most
          current inventory.
        </p>
      </>
    ),
  },
  {
    id: "third-party",
    title: "4. Third-party services",
    body: (
      <>
        <p>
          Our Site embeds or relies on the following third-party services. Each service is
          governed by its own privacy and cookie policies, which we encourage you to review:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>PostHog</strong> — product analytics and session recording.{" "}
            <a href="https://posthog.com/privacy" className="underline" target="_blank" rel="noreferrer">posthog.com/privacy</a>
          </li>
          <li>
            <strong>Google Maps</strong> — office-location embed on our Contact page.{" "}
            <a href="https://policies.google.com/privacy" className="underline" target="_blank" rel="noreferrer">policies.google.com/privacy</a>
          </li>
          <li>
            <strong>Google Fonts</strong> — web-font delivery for our brand typography.
          </li>
          <li>
            <strong>Unsplash</strong> — image delivery for editorial photography.
          </li>
          <li>
            <strong>Resend</strong> — transactional email delivery when you submit a form. Resend does not set cookies on your browser.{" "}
            <a href="https://resend.com/legal/privacy-policy" className="underline" target="_blank" rel="noreferrer">resend.com/legal/privacy-policy</a>
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "your-choices",
    title: "5. Your choices",
    body: (
      <>
        <p>You can control or disable cookies in several ways:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Browser settings</strong> — most browsers allow you to block or delete cookies.
            Consult your browser&rsquo;s help pages for instructions. Note that disabling cookies
            may impact your experience on this and other websites.
          </li>
          <li>
            <strong>Do Not Track</strong> — we honour your browser&rsquo;s
            &ldquo;Do Not Track&rdquo; signal where feasible, although no consistent industry
            standard exists.
          </li>
          <li>
            <strong>Clear localStorage</strong> — clearing site data in your browser removes the
            consent record; the cookie banner will reappear on your next visit.
          </li>
          <li>
            <strong>Opt out of PostHog</strong> — clear your browser storage or contact us to have
            your PostHog data deleted.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "consent",
    title: "6. Consent",
    body: (
      <>
        <p>
          When you first visit the Site, you will see a cookie banner at the bottom of the screen.
          By clicking &ldquo;Accept&rdquo; you consent to the use of analytics cookies as described
          above. Strictly-necessary cookies do not require consent because they are essential to
          operate the Site.
        </p>
        <p>
          You can withdraw consent at any time by clearing your browser storage or by emailing
          us at <a href="mailto:Sales@ashrenltd.com" className="underline">Sales@ashrenltd.com</a>.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "7. Changes to this policy",
    body: (
      <>
        <p>
          We may update this Cookie Policy to reflect changes in the technologies we use or in
          applicable law. The effective date at the top of this page shows when it was last
          revised.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "8. Contact us",
    body: (
      <>
        <p>
          Questions about this Cookie Policy can be directed to:
        </p>
        <p>
          <strong>Ashren Consulting Ltd.</strong><br />
          101, 2529 Kingsway<br />
          Vancouver, BC V5R 5H3<br />
          Canada<br />
          Email: <a href="mailto:Sales@ashrenltd.com" className="underline">Sales@ashrenltd.com</a><br />
          Phone: <a href="tel:+12364850134" className="underline">+1 236-485-0134</a>
        </p>
      </>
    ),
  },
];

export default function CookiePolicy() {
  return (
    <LegalPage
      eyebrow="Legal · Cookies"
      title="Cookie Policy"
      subtitle="What cookies and similar technologies we use on www.ashrenltd.com, why we use them, and how you can control them."
      effectiveDate="May 4, 2026"
      sections={sections}
      testId="cookie-policy-page"
    />
  );
}
